/** @format */

import { Directive, inject, input, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

/**
 * Put this directive on the parent of an element using the {@link InViewportDirective} to observe
 * intersection. This allows multiple InViewportDirectives to share a single IntersectionObserver
 * instance created in the parent directive.
 */
@Directive({
  selector: '[sharedUtilsDirectiveInViewportParent]',
})
export class InViewportParentDirective implements OnInit, OnDestroy {
  private readonly platformId = inject(PLATFORM_ID);

  readonly intersectionObserverOptions = input<IntersectionObserverInit>();

  private readonly elementCallbackMap = new Map<Element, (inViewport: boolean) => void>();
  private observer: IntersectionObserver;

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.observer = new IntersectionObserver(
        (entries) => this.observerCallback(entries),
        this.intersectionObserverOptions(),
      );
    }
  }

  public add(element: Element, callback: (inViewport: boolean) => void) {
    this.elementCallbackMap.set(element, callback);
    this.observer?.observe(element);
  }

  public remove(element: Element) {
    this.elementCallbackMap.delete(element);
    this.observer?.unobserve(element);
  }

  ngOnDestroy() {
    this.elementCallbackMap.clear();
    this.observer?.disconnect();
  }

  private observerCallback(entries: IntersectionObserverEntry[]) {
    for (const entry of entries) {
      const callback = this.elementCallbackMap.get(entry.target);
      if (callback) {
        callback(entry.isIntersecting);
      }
    }
  }
}
